import {noop} from '@benchling/js-utils';
import moment from 'moment';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {CREATE_ANALYSIS_NO_PERMISSION_MESSAGE} from 'studies/typed/utils/analysis/analysisUtils';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcessDevelopmentAnalysisModalBanner from 'studies/typed/utils/analysis/ProcessDevelopmentAnalysisModalBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import runModal from 'ui-library/runModal';
import * as DateUtil from 'util/DateUtil';

/**
 * Props for AnalyzeProcedureRunButton component.
 *
 * @package exported for testing.
 */
export interface AnalyzeProcedureRunButtonProps {
  procedureRunId: string;
  procedureRunName: string;
  procedureType: ProcedureType;
  studyId: string;
  studyName: string;
  studyHumanId: string;
  studyType: StudyType;
  actionOrigin: ActionOrigin;
}

export default function AnalyzeProcedureRunButton(props: AnalyzeProcedureRunButtonProps): JSX.Element {
  let onClickHandler: () => void = noop;
  assert(props.procedureRunId != null, 'Procedure Run is not selected');

  const {
    studyId,
    studyName,
    studyHumanId,
    studyType,
    procedureRunId,
    procedureRunName,
    procedureType,
    actionOrigin,
  } = props;
  const getInitialAnalysisName = (): string =>
    `${studyName}_${procedureRunName}_${DateUtil.readableTime(moment(), {
      showTime: true,
    })}`;

  onClickHandler = async () => {
    await runModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {actionOrigin, studyType}),
      initialStudySearchItems: [{id: studyId, name: studyName, humanId: studyHumanId}],
      disableEditStudies: true,
      title: 'Generate analysis',
      initialAnalysisName: getInitialAnalysisName,
      headerInlineBanner: <ProcessDevelopmentAnalysisModalBanner />,
      hideDataSharingWarningBanner: true,
      initialSeedDataConfig: {
        type: SeedDataConfigType.PROCEDURE_RUN,
        data: {
          procedureRunId,
        },
      },
    });
  };

  const buttonDisabled: boolean = !isFeatureEnabledForProcedureType(
    procedureType,
    ProceduresFeature.CREATE_ANALYSIS
  );

  return (
    <Tooltip text={CREATE_ANALYSIS_NO_PERMISSION_MESSAGE} isDisabled={!buttonDisabled}>
      <Button
        dataTestAttrs={{
          component: 'AnalyzeProcedureRunButton',
          element: 'root',
        }}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        onClick={onClickHandler}
        text="Analyze"
        icon={ModelTypeConstants.ANALYSIS}
        disabled={buttonDisabled}
      />
    </Tooltip>
  );
}
